import { useEffect, useMemo, useRef, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { SelectInput, TextInput, geocodeText, showToast } from '@my/ui';
import {
  useCreateLocationAddress,
  useOrganisationCarriers,
  useOrganisationRoutes,
  useParties,
} from '@my/directory-api/hooks';
import type { Address } from '@my/directory-api';
import type { BookingTypeValue, ClearanceStatusValue } from '@my/tms-api';
import {
  applyBookingTypeChange,
  BOOKING_TYPE_OPTIONS,
  bookingTypeShape,
} from '../lib/bookingForm';
import {
  bookingRouteOptionLabel,
  bookingRouteOptionValue,
  buildPortAddressCode,
  indexEntries,
  matchAddressByCode,
  matchAddressForPort,
  parseBookingRouteOptionValue,
  resolveOrganisationRoutes,
} from '../lib/route';
import type { BookingFormValues } from '../lib/types';
import {
  FerryBookingFields,
  FerrySailingSelect,
  type FerryCargoHint,
} from '../../ferry/components/FerrySailingFields';
import { useFerryCrossing } from '../../ferry/useFerryCrossing';
import { useShippingIntegration } from '../../ferry/shippingIntegration';
import { useCrossingCatalogue } from '../../ferry/useCrossingCatalogue';
import { ferryCarrierCodeFor } from '../../ferry/catalogue';
import { BookingMap } from './BookingMap';
import { portLatLngByName } from '../../../utils/portCoordinates';

import { Combobox, type ComboboxOption } from './inputs';

/**
 * Accompaniment row inside `BookingFormFields`. Cells (Accompanied,
 * Drivers, Vehicle reg, Trailer / Container BIC) appear based on
 * `bookingTypeShape(values.bookingType)` — the column count adapts so
 * the row never renders placeholder gaps.
 */
function BookingAccompanimentRow({
  values,
  onChange,
  disabled,
  accompanimentLocked = false,
}: {
  values: BookingFormValues;
  onChange: (patch: Partial<BookingFormValues>) => void;
  disabled: boolean;
  /** Held by the carrier booking: it decides the vehicle type sent there. */
  accompanimentLocked?: boolean;
}) {
  const shape = bookingTypeShape(values.bookingType);
  const visibleCount =
    1 +
    (shape.hasDrivers ? 1 : 0) +
    (shape.hasVehicle ? 1 : 0) +
    (shape.hasTrailer ? 1 : 0);
  // Tailwind needs static class names — explicit ternary picks one of
  // four pre-known grid-cols classes so the compiler keeps each in
  // the output bundle.
  const gridCols =
    visibleCount === 4
      ? 'md:grid-cols-4'
      : visibleCount === 3
        ? 'md:grid-cols-3'
        : visibleCount === 2
          ? 'md:grid-cols-2'
          : 'md:grid-cols-1';
  const driversDisabled = disabled || !values.isAccompanied;
  const vehicleDisabled = disabled || !values.isAccompanied;
  return (
    <div className={`grid grid-cols-1 gap-3 ${gridCols}`}>
      <SelectInput
        label="Accompanied"
        name="booking-form-accompanied"
        value={values.isAccompanied ? 'yes' : 'no'}
        options={[
          { value: 'yes', label: 'Accompanied' },
          { value: 'no', label: 'Unaccompanied (trailer only)' },
        ]}
        // Locked when the booking category is by-definition unaccompanied
        // (Trailer). Stays visible — operator sees the constraint instead
        // of wondering why the cell is gone.
        disabled={disabled || shape.forceUnaccompanied || accompanimentLocked}
        // The carrier's vehicle type follows the accompanied choice; cleared so it is picked again.
        onChange={(e) =>
          onChange({
            isAccompanied: e.target.value === 'yes',
            ferryVehicleTypeCode: '',
            ferryLengthMetres: '',
          })
        }
      />
      {shape.hasDrivers && (
        <TextInput
          label="Drivers"
          name="booking-form-drivers"
          inputMode="numeric"
          value={values.numberOfDrivers}
          disabled={driversDisabled}
          onChange={(e) =>
            onChange({ numberOfDrivers: e.target.value.replace(/[^\d]/g, '') })
          }
        />
      )}
      {shape.hasVehicle && (
        <TextInput
          label="Vehicle reg"
          name="booking-form-vehicle"
          autoComplete="off"
          value={values.vehicleRegistration}
          disabled={vehicleDisabled}
          maxLength={32}
          onChange={(e) =>
            onChange({ vehicleRegistration: e.target.value.toUpperCase() })
          }
        />
      )}
      {shape.hasTrailer && (
        <TextInput
          // Label flips to "Container BIC" for container booking types —
          // operator types the 11-char container identifier rather than
          // a chassis number.
          label={shape.trailerLabel}
          name="booking-form-trailer"
          autoComplete="off"
          value={values.trailerNumber}
          disabled={disabled}
          maxLength={32}
          onChange={(e) => onChange({ trailerNumber: e.target.value.toUpperCase() })}
        />
      )}
    </div>
  );
}

/**
 * Shared field set used by TaskCreateForm's booking branch AND
 * BookingMetadataEditPanel — create and edit surfaces stay visually
 * identical. Two-pane layout: form on the left (3/5), BookingMap on
 * the right (2/5).
 *
 * The crossing list (the catalogue file plus My.Ferry's routes) is cached
 * for a day, so repeated mounts are cheap.
 */
export function BookingFormFields({
  values,
  onChange,
  addresses,
  organisationId,
  disabled,
  mode = 'edit',
  cargo = null,
}: {
  values: BookingFormValues;
  onChange: (patch: Partial<BookingFormValues>) => void;
  addresses: Address[];
  /** Owning org — null until resolved. Route picker stays empty when null. */
  organisationId: string | null;
  disabled: boolean;
  /**
   * Only a new booking is created at the carrier (the Ferry fields appear for
   * a linked crossing). Editing a booked one may move its sailing; the edit
   * panel sends that to the carrier as an amend.
   */
  mode?: 'create' | 'edit';
  /** The order's load, for the carrier booking's defaults; null when the surface has no order. */
  cargo?: FerryCargoHint | null;
}) {
  const departureAddress = useMemo(
    () => addresses.find((a) => a.id === values.departureAddressId) ?? null,
    [addresses, values.departureAddressId],
  );
  const arrivalAddress = useMemo(
    () => addresses.find((a) => a.id === values.arrivalAddressId) ?? null,
    [addresses, values.arrivalAddressId],
  );

  const orgRoutesQuery = useOrganisationRoutes(organisationId);
  const orgCarriersQuery = useOrganisationCarriers(organisationId);
  // Routes and carriers by key, whichever list a pick came from: My.Ferry, or
  // the catalogue file for picks saved before the move.
  const { routeByKey: routeCatalogueByKey, carrierByKey: carrierCatalogueByKey } =
    useCrossingCatalogue();
  // Supplier parties resolve the carrier combobox label when the
  // catalogue lookup misses (auto-created party has `name = catalogue
  // carrier name`, so they agree in practice).
  const suppliersQuery = useParties(organisationId, 'Supplier');
  const supplierByPartyId = useMemo(
    () => indexEntries(suppliersQuery.data ?? [], (p) => p.id),
    [suppliersQuery.data],
  );
  // Reverse lookup: catalogue carrierKey → OrganisationCarrier.partyId.
  // Used by the route auto-fill to stamp the carrier party id. A carrier is
  // registered under both its key styles, so a route picked from Ferry's list
  // still finds the party of a carrier saved under the file's key.
  const partyIdByCarrierKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of orgCarriersQuery.data ?? []) {
      if (!c.partyId) continue;
      map.set(c.catalogueKey, c.partyId);
      const alias = carrierCatalogueByKey.get(c.catalogueKey)?.linkedKey;
      if (alias && !map.has(alias)) map.set(alias, c.partyId);
    }
    return map;
  }, [orgCarriersQuery.data, carrierCatalogueByKey]);

  const resolvedRoutes = useMemo(
    () => resolveOrganisationRoutes(orgRoutesQuery.data ?? [], routeCatalogueByKey),
    [orgRoutesQuery.data, routeCatalogueByKey],
  );

  // A Ferry route is one direction and one option. A catalogue-file entry
  // stands for both directions, so it surfaces forward + reverse legs.
  // Sorted alphabetically so operators can scan quickly.
  const routeOptions = useMemo<ComboboxOption[]>(
    () =>
      resolvedRoutes
        .flatMap(({ entry }) => {
          const carrier = carrierCatalogueByKey.get(entry.carrierKey);
          const forward = {
            value: bookingRouteOptionValue(entry.key, false),
            label: bookingRouteOptionLabel(entry, carrier, false),
          };
          if (!entry.bidirectional) return [forward];
          return [
            forward,
            {
              value: bookingRouteOptionValue(entry.key, true),
              label: bookingRouteOptionLabel(entry, carrier, true),
            },
          ];
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [resolvedRoutes, carrierCatalogueByKey],
  );

  const carrierOptions = useMemo<ComboboxOption[]>(() => {
    const rows = orgCarriersQuery.data ?? [];
    const options = rows
      .map<{ partyId: string; label: string } | null>((c) => {
        if (!c.partyId) return null;
        const catalogueLabel = carrierCatalogueByKey.get(c.catalogueKey)?.name;
        const partyLabel = supplierByPartyId.get(c.partyId)?.name;
        const label = catalogueLabel || partyLabel || c.catalogueKey;
        return { partyId: c.partyId, label };
      })
      .filter((o): o is { partyId: string; label: string } => o !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
    return options.map(({ partyId, label }) => ({ value: partyId, label }));
  }, [orgCarriersQuery.data, carrierCatalogueByKey, supplierByPartyId]);

  // Agents are operator-managed via the standard Parties screen — no
  // curated catalogue. The currently-picked agent always surfaces even
  // if it doesn't match the subtype filter (defensive: subtype can be
  // changed after the booking was saved).
  const agentOptions = useMemo<ComboboxOption[]>(() => {
    const rows = suppliersQuery.data ?? [];
    const filtered = rows
      .filter((p) => p.subtype === 'ShippingAgency' || p.id === values.agentPartyId)
      .sort((a, b) => a.name.localeCompare(b.name));
    return filtered.map((p) => ({ value: p.id, label: p.name }));
  }, [suppliersQuery.data, values.agentPartyId]);

  // Route is the mandatory driver for ports. Clearing it resets both ports.
  const [selectedRouteKey, setSelectedRouteKey] = useState<string>('');
  // Port names from the route catalogue — shown directly in the read-only
  // TextInput fields so the operator always sees the port name even if the
  // org's location catalogue has no matching address record yet.
  const [routePortLabels, setRoutePortLabels] = useState({ departure: '', arrival: '' });

  // What My.Ferry knows about the picked crossing. A linked crossing books at
  // the carrier on save; every other crossing stays the manual booking it is.
  const picked = useMemo(() => {
    if (!selectedRouteKey) return { entry: null, reversed: false, carrier: null };
    const { catalogueKey, reversed } = parseBookingRouteOptionValue(selectedRouteKey);
    const entry = routeCatalogueByKey.get(catalogueKey) ?? null;
    const carrier = entry ? (carrierCatalogueByKey.get(entry.carrierKey) ?? null) : null;
    return { entry, reversed, carrier };
  }, [selectedRouteKey, routeCatalogueByKey, carrierCatalogueByKey]);
  // Once booked at the carrier the route, booking type and accompaniment are its.
  const ferryLinked = !!values.ferryBookingId;
  // Ferry is asked about the crossing only when a booking will go through it:
  // the integration and the switch on when creating, the booking already at
  // Ferry when editing (whatever the integration says, so it can be moved).
  const { enabled: integrationEnabled } = useShippingIntegration(organisationId);
  const crossing = useFerryCrossing(
    organisationId,
    picked.entry,
    picked.reversed,
    picked.carrier,
    mode === 'edit' ? ferryLinked : values.ferryBook && integrationEnabled,
  );
  // The sailing can move while Ferry can check the new time against the timetable.
  const ferryAmend = mode === 'edit' && ferryLinked && crossing.enabled;
  // The Integration switch (Off) keeps even a linked crossing a TMS-only booking.
  const ferryActive = mode === 'create' && values.ferryBook && crossing.enabled;

  // The picked crossing's carrier at Ferry, from the catalogue alone, so the
  // form can tell whether the organisation holds an account there before
  // anything is asked of Ferry.
  const pickedCarrierCode = ferryCarrierCodeFor(picked.carrier) ?? '';
  useEffect(() => {
    if (mode !== 'create' || values.ferryCarrierCode === pickedCarrierCode) return;
    onChange({ ferryCarrierCode: pickedCarrierCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, pickedCarrierCode, values.ferryCarrierCode]);

  // Keep the Ferry side of the form in step with the picked crossing: the ids,
  // the departure port's clock, and the account (picked for the operator when
  // the organisation has only one).
  useEffect(() => {
    if (mode !== 'create') return;
    const next =
      crossing.enabled && values.ferryBook
        ? {
          ferryCarrierId: crossing.carrierId ?? '',
          ferryRouteCode: crossing.routeCode ?? '',
          ferryOriginZone: crossing.originZone,
          ferryCredentialRequired: crossing.credentialRequired,
          // A pick is kept only while it is one of this carrier's accounts.
          ferryCredentialId: crossing.credentialRequired
            ? crossing.credentials.some((c) => c.id === values.ferryCredentialId)
              ? values.ferryCredentialId
              : ''
            : (crossing.credentials[0]?.id ?? ''),
        }
      : {
          ferryCarrierId: '',
          ferryRouteCode: '',
          ferryOriginZone: '',
          ferryCredentialRequired: false,
          ferryCredentialId: '',
        };
    const routeChanged = next.ferryRouteCode !== values.ferryRouteCode;
    const changed =
      routeChanged ||
      next.ferryCarrierId !== values.ferryCarrierId ||
      next.ferryOriginZone !== values.ferryOriginZone ||
      next.ferryCredentialRequired !== values.ferryCredentialRequired ||
      next.ferryCredentialId !== values.ferryCredentialId;
    if (!changed) return;
    // A different route is a different timetable, maybe a different carrier:
    // the sailing and the vehicle type are picked again.
    onChange(
      routeChanged
        ? {
            ...next,
            ferrySailingUtc: '',
            sailTime: '',
            ferryVehicleTypeCode: '',
            ferryLengthMetres: '',
          }
        : next,
    );
    // `onChange` is a fresh closure each render; the values it needs are listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    values.ferryBook,
    crossing.enabled,
    crossing.carrierId,
    crossing.routeCode,
    crossing.originZone,
    crossing.credentialRequired,
    crossing.credentials,
    values.ferryCarrierId,
    values.ferryRouteCode,
    values.ferryOriginZone,
    values.ferryCredentialRequired,
    values.ferryCredentialId,
  ]);

  // Coordinates for the map pins. Prefer stored lat/lng on the address
  // record; fall back to PORT_COORDS lookup by name (covers addresses
  // auto-created from the route catalogue without geocoding).
  const depMapCoords = useMemo(() => {
    const a = departureAddress;
    if (a?.latitude != null && a?.longitude != null) {
      return { lat: a.latitude as number, lng: a.longitude as number };
    }
    const name = routePortLabels.departure || a?.name;
    return portLatLngByName(name);
  }, [departureAddress, routePortLabels.departure]);

  const arrMapCoords = useMemo(() => {
    const a = arrivalAddress;
    if (a?.latitude != null && a?.longitude != null) {
      return { lat: a.latitude as number, lng: a.longitude as number };
    }
    const name = routePortLabels.arrival || a?.name;
    return portLatLngByName(name);
  }, [arrivalAddress, routePortLabels.arrival]);

  // Pre-select the saved route when editing an existing booking.
  // The booking's own route row comes first: two carriers can share the same
  // port names, so a name match alone can land on the wrong carrier. A booking
  // from before route-field persistence has no row and falls back to matching
  // the saved departure/arrival address names against the catalogue port
  // names. Runs once after catalogue + org-routes data loads; the ref prevents
  // re-running after the operator picks a different route.
  const routeInitializedRef = useRef(false);
  useEffect(() => {
    if (routeInitializedRef.current || selectedRouteKey) return;
    const routes = orgRoutesQuery.data;
    if (!routes || !routeCatalogueByKey.size) return;
    if (!values.departureAddressId && !values.arrivalAddressId) return;

    const depAddr = addresses.find((a) => a.id === values.departureAddressId) ?? null;
    const arrAddr = addresses.find((a) => a.id === values.arrivalAddressId) ?? null;
    if (!depAddr && !arrAddr) return;

    const depName = depAddr?.name.toLowerCase() ?? '';
    const arrName = arrAddr?.name.toLowerCase() ?? '';

    const savedRow = values.carrierRouteId
      ? routes.find((r) => r.id === values.carrierRouteId)
      : undefined;
    const savedEntry = savedRow ? routeCatalogueByKey.get(savedRow.catalogueKey) : undefined;
    if (savedRow && savedEntry) {
      // A file entry stands for both directions: the saved route code says
      // which when it is one of the entry's Ferry codes, else the departure port.
      const code = values.routeCode;
      const reversed =
        savedEntry.bidirectional &&
        (code && code === savedEntry.ferryReverseRouteCode
          ? true
          : code && code === savedEntry.ferryRouteCode
            ? false
            : depName === savedEntry.destinationPortName.toLowerCase());
      routeInitializedRef.current = true;
      setSelectedRouteKey(bookingRouteOptionValue(savedRow.catalogueKey, reversed));
      setRoutePortLabels(
        reversed
          ? { departure: savedEntry.destinationPortName, arrival: savedEntry.originPortName }
          : { departure: savedEntry.originPortName, arrival: savedEntry.destinationPortName },
      );
      return;
    }

    const resolved = resolveOrganisationRoutes(routes, routeCatalogueByKey);
    for (const { row, entry } of resolved) {
      const originLower = entry.originPortName.toLowerCase();
      const destLower = entry.destinationPortName.toLowerCase();

      // Denormalised route snapshot — same regardless of leg direction
      // (code / name / typical durations are direction-agnostic). Backfilled
      // onto the form values so a save persists them even for bookings that
      // predate route-field persistence.
      const routeFields = {
        carrierRouteId: row.id,
        routeCode: entry.code ?? '',
        routeName: entry.name,
        typicalDurationMinutes: entry.typicalDurationMinutes ?? null,
        typicalCheckInMinutes: entry.typicalCheckInMinutes ?? null,
        typicalDriveOffMinutes: entry.typicalDriveOffMinutes ?? null,
      };

      // Forward leg
      if (depName === originLower && arrName === destLower) {
        routeInitializedRef.current = true;
        setSelectedRouteKey(bookingRouteOptionValue(row.catalogueKey, false));
        setRoutePortLabels({ departure: entry.originPortName, arrival: entry.destinationPortName });
        onChange(routeFields);
        return;
      }
      // Reverse leg — only a catalogue-file entry has one.
      if (entry.bidirectional && depName === destLower && arrName === originLower) {
        routeInitializedRef.current = true;
        setSelectedRouteKey(bookingRouteOptionValue(row.catalogueKey, true));
        setRoutePortLabels({ departure: entry.destinationPortName, arrival: entry.originPortName });
        onChange(routeFields);
        return;
      }
    }
    // No match found — allow retry when more data arrives (don't set ref)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgRoutesQuery.data, routeCatalogueByKey.size, addresses, values.carrierRouteId, values.departureAddressId, values.arrivalAddressId]);

  // Display label for each port:
  //   - Route selected  → catalogue port name (always correct)
  //   - Edit mode       → look up label from org's address catalogue by stored ID
  const departurePortLabel = useMemo(() => {
    if (selectedRouteKey) return routePortLabels.departure;
    const a = addresses.find((addr) => addr.id === values.departureAddressId);
    return a ? (a.city ? `${a.name} — ${a.city}` : a.name) : '';
  }, [selectedRouteKey, routePortLabels.departure, addresses, values.departureAddressId]);

  const arrivalPortLabel = useMemo(() => {
    if (selectedRouteKey) return routePortLabels.arrival;
    const a = addresses.find((addr) => addr.id === values.arrivalAddressId);
    return a ? (a.city ? `${a.name} — ${a.city}` : a.name) : '';
  }, [selectedRouteKey, routePortLabels.arrival, addresses, values.arrivalAddressId]);

  const createLocationMutation = useCreateLocationAddress();
  // Places library for geocoding a carrier's port terminal when we
  // auto-create its Location row. Null until the Maps script loads (or
  // forever if no API key is configured) — geocodeText handles null by
  // resolving to null, so the create still happens, just without coords.
  const placesLib = useMapsLibrary('places');

  const handleRoutePick = async (optionValue: string) => {
    setSelectedRouteKey(optionValue);
    if (!optionValue) {
      // Route cleared — reset both ports + the denormalised route fields
      // so the read-only fields go blank and the save clears the FK.
      setRoutePortLabels({ departure: '', arrival: '' });
      onChange({
        departureAddressId: '',
        arrivalAddressId: '',
        carrierRouteId: '',
        routeCode: '',
        routeName: '',
        typicalDurationMinutes: null,
        typicalCheckInMinutes: null,
        typicalDriveOffMinutes: null,
      });
      return;
    }
    const { catalogueKey, reversed } = parseBookingRouteOptionValue(optionValue);
    const route = routeCatalogueByKey.get(catalogueKey);
    if (!route) return;
    // The org's opted-in route row backing this catalogue key — its id is
    // persisted as OrderBooking.CarrierRouteId. The catalogue entry supplies
    // the denormalised code / name / typical durations snapshot.
    const orgRoute = (orgRoutesQuery.data ?? []).find((r) => r.catalogueKey === catalogueKey);
    // Reverse leg swaps which end of the catalogue entry feeds which port.
    const originName = reversed ? route.destinationPortName : route.originPortName;
    const originCountry = reversed ? route.destinationCountryIsoCode : route.originCountryIsoCode;
    const destinationName = reversed ? route.originPortName : route.destinationPortName;
    const destinationCountry = reversed
      ? route.originCountryIsoCode
      : route.destinationCountryIsoCode;
    const carrierPartyId = partyIdByCarrierKey.get(route.carrierKey) ?? '';

    // Carrier identity drives both the per-terminal address code
    // ("{carrierCode}-{portCode}") and the Google query we geocode the
    // terminal with ("{carrierName} {portName}"), so two carriers serving
    // the same physical port resolve to distinct terminal rows.
    const carrier = carrierCatalogueByKey.get(route.carrierKey);
    const carrierCode = carrier?.code ?? null;
    const carrierName = carrier?.name ?? '';
    const originPortCode = reversed ? route.destinationPortCode : route.originPortCode;
    const destinationPortCode = reversed ? route.originPortCode : route.destinationPortCode;

    // Show port names from the route catalogue immediately — the operator
    // sees them whether or not the address records exist yet.
    setRoutePortLabels({ departure: originName, arrival: destinationName });

    // Resolve one route end to an Address id. Reuse the carrier's existing
    // terminal (matched by code — NOT name, so two carriers at the same port
    // stay distinct rows); otherwise geocode the carrier's terminal via
    // Google Places and create a fully-populated Location row (code + coords
    // + formatted address + place id). Falls back to the legacy name-only
    // match/create when there's no stable code to key on (catalogue port code
    // or carrier international code missing).
    const resolvePortAddressId = async (
      portName: string,
      portCountry: string,
      portCode: string | null | undefined,
    ): Promise<string> => {
      const code = buildPortAddressCode(carrierCode, portCode);

      if (code) {
        const existing = matchAddressByCode(addresses, code);
        if (existing) return existing.id;
        if (!organisationId) return '';

        // Carrier-specific terminal first, then the bare port, so we still
        // capture coordinates when Google doesn't index the named terminal.
        // A null result just means "not geocodable" — we create the coded
        // row anyway, only without coordinates.
        const geo =
          (await geocodeText(placesLib, `${carrierName} ${portName}`, portCountry)) ??
          (await geocodeText(placesLib, portName, portCountry));

        return createLocationMutation.mutateAsync({
          organisationId,
          addressType: 'Location',
          name: portName,
          code,
          countryIsoCode: portCountry,
          city: geo?.city || null,
          postcode: geo?.postcode || null,
          latitude: geo ? geo.latitude : null,
          longitude: geo ? geo.longitude : null,
          fullAddress: geo?.formattedAddress || null,
          placeId: geo?.placeId || null,
        });
      }

      // Legacy path: no code to dedupe on — match by name+country, create a
      // bare name-only Location when nothing matches (pre-port-code behaviour).
      const existing = matchAddressForPort(addresses, portName, portCountry);
      if (existing) return existing.id;
      if (!organisationId) return '';
      return createLocationMutation.mutateAsync({
        organisationId,
        addressType: 'Location',
        name: portName,
        countryIsoCode: portCountry,
      });
    };

    // Sequential — the second create shouldn't race the first, and the
    // locations query is invalidated on success so the address list (and the
    // BookingMap pins) refresh after the round-trip.
    let departureId = '';
    let arrivalId = '';
    try {
      departureId = await resolvePortAddressId(originName, originCountry, originPortCode);
      arrivalId = await resolvePortAddressId(
        destinationName,
        destinationCountry,
        destinationPortCode,
      );
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to create port location record.',
      });
    }

    onChange({
      carrierPartyId,
      departureAddressId: departureId,
      arrivalAddressId: arrivalId,
      carrierRouteId: orgRoute?.id ?? '',
      routeCode: route.code ?? '',
      routeName: route.name,
      typicalDurationMinutes: route.typicalDurationMinutes ?? null,
      typicalCheckInMinutes: route.typicalCheckInMinutes ?? null,
      typicalDriveOffMinutes: route.typicalDriveOffMinutes ?? null,
    });
  };

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <TextInput
            label="Sail date *"
            type="date"
            value={values.sailDate}
            disabled={disabled || (ferryLinked && !ferryAmend)}
            // A different day is a different timetable; the sailing is matched again when it loads.
            onChange={(e) =>
              onChange({
                sailDate: e.target.value,
                ferrySailingUtc: '',
                ferryDayDepartures: '',
              })
            }
          />
          {ferryActive || ferryAmend ? (
            <FerrySailingSelect
              organisationId={organisationId}
              crossing={crossing}
              values={values}
              onChange={onChange}
              disabled={disabled}
            />
          ) : (
            <TextInput
              label="Sail time"
              type="time"
              value={values.sailTime}
              disabled={disabled || ferryLinked}
              onChange={(e) => onChange({ sailTime: e.target.value })}
            />
          )}
          <SelectInput
            label="Booking type *"
            name="booking-form-type"
            value={values.bookingType}
            options={BOOKING_TYPE_OPTIONS}
            disabled={disabled || ferryLinked}
            onChange={(e) =>
              onChange(applyBookingTypeChange(e.target.value as BookingTypeValue))
            }
          />
        </div>

        <Combobox
          label="Route *"
          value={selectedRouteKey}
          options={routeOptions}
          onChange={handleRoutePick}
          disabled={disabled || ferryLinked}
          placeholder={
            routeOptions.length === 0
              ? 'No routes configured — add some in Settings → Configuration → Carriers & Routes.'
              : 'Select a route — departure and arrival ports are filled automatically.'
          }
          allowClear
        />

        {/* Ports are always read-only — driven entirely by the route selection above.
            The display value comes from the route catalogue (port name) when a route
            is active, or from the org's address catalogue label in edit mode. */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label="Departure port *"
            name="booking-form-departure-port"
            value={departurePortLabel}
            readOnly
            placeholder="Filled automatically from the selected route"
          />
          <TextInput
            label="Arrival port *"
            name="booking-form-arrival-port"
            value={arrivalPortLabel}
            readOnly
            placeholder="Filled automatically from the selected route"
          />
        </div>

        {ferryLinked && (
          <p className="text-[11px] text-slate-500">
            Route, booking type and accompaniment are held by the carrier booking; to change them,
            delete the booking and book again.
          </p>
        )}
        {mode === 'create' && values.ferryBook && crossing.linked && !crossing.enabled && (
          <p className="text-[11px] text-amber-700">
            {crossing.isLoading ? 'Checking with the ferry service…' : crossing.unavailableReason}
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Combobox
            label="Carrier"
            value={values.carrierPartyId}
            options={carrierOptions}
            onChange={(v) => onChange({ carrierPartyId: v })}
            placeholder={
              carrierOptions.length === 0
                ? 'No carriers configured — add some in Settings → Configuration → Carriers & Routes.'
                : 'Pick the shipping carrier…'
            }
            allowClear
          />
          <Combobox
            label="Agent"
            value={values.agentPartyId}
            options={agentOptions}
            onChange={(v) => onChange({ agentPartyId: v })}
            placeholder={
              agentOptions.length === 0
                ? 'No shipping agents — flag a supplier as Shipping Agency in Parties.'
                : 'Pick the shipping agent…'
            }
            allowClear
          />
          <TextInput
            label="Carrier reference"
            name="booking-form-carrier-ref"
            autoComplete="off"
            value={values.carrierReference}
            disabled={disabled}
            maxLength={64}
            onChange={(e) => onChange({ carrierReference: e.target.value })}
          />
        </div>

        {/* What the carrier needs beyond the TMS fields, as rows like the rest. */}
        {ferryActive && (
          <FerryBookingFields
            crossing={crossing}
            values={values}
            onChange={onChange}
            disabled={disabled}
            cargo={cargo}
          />
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label="Vessel"
            name="booking-form-vessel"
            autoComplete="off"
            value={values.vessel}
            disabled={disabled}
            maxLength={64}
            onChange={(e) => onChange({ vessel: e.target.value })}
          />
          <TextInput
            label="Voyage number"
            name="booking-form-voyage"
            autoComplete="off"
            value={values.voyageNumber}
            disabled={disabled}
            maxLength={32}
            onChange={(e) => onChange({ voyageNumber: e.target.value })}
          />
        </div>

        <BookingAccompanimentRow
          values={values}
          onChange={onChange}
          disabled={disabled}
          accompanimentLocked={ferryLinked}
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label="Customs reference"
            name="booking-form-customs"
            autoComplete="off"
            value={values.customsReference}
            disabled={disabled}
            maxLength={64}
            onChange={(e) => onChange({ customsReference: e.target.value })}
          />
          <SelectInput
            label="Clearance status"
            name="booking-form-clearance"
            value={values.clearanceStatus}
            options={[
              { value: '', label: '—' },
              { value: 'Required', label: 'Required' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Cleared', label: 'Cleared' },
            ]}
            disabled={disabled}
            onChange={(e) =>
              onChange({ clearanceStatus: e.target.value as ClearanceStatusValue | '' })
            }
          />
        </div>

        <TextInput
          label="Notes"
          value={values.notes}
          disabled={disabled}
          onChange={(e) => onChange({ notes: e.target.value })}
        />
      </div>

      <BookingMap departure={depMapCoords} arrival={arrMapCoords} />
    </div>
  );
}
